import {
  Box,
  Button,
  TextField,
  Stepper,
  Step,
  StepButton,
  Typography,
  Paper,
  Snackbar,
  Alert,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import {
  ContactsModal,
  TemplatesModal,
  CampaignCreationProgressModal,
  AudienceSection,
  ScheduleSection,
  SummarySection,
} from "..";
import { useSelector } from "react-redux";
import { createCampaign, validateContactFile } from "../../../../actions/campaigns/campaigns";
import { MAX_CONTACTS_PER_TEMPLATE, UPLOAD_TYPES } from "../constants";
import { useCampaignDraft } from "../../../../hooks/useCampaignDraft";
import {
  formatApiDate,
  isProgrammingDateExpired,
} from "../../../../utils/dateFormatters-campaign.utils";
import { getPricingQuote } from "../../../../actions/campaigns/pricing.actions";
import { extractTemplateVariables } from "../../../../utils/template-variable-extractor.utils";

export const CampaignForm = ({ onSubmit, onCancel, initialData }) => {
  const [activeStep, setActiveStep] = useState(0);
  const steps = ["Datos Básicos", "Audiencia", "Programación", "Resumen"];

  const [formData, setFormData] = useState({
    campaignName: "",
    alias: "",
    template: "",
    descripcion: "",
    audienceType: "file",
    scheduleType: "mismo_tiempo",
    sendType: "",
    scheduledDateTime: null,
  });

  const { id_customer, name, gupshup_app_id } = useSelector(
    (state) => state.agent
  );
  const [contactsModalOpen, setContactsModalOpen] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [currentDateTime, setCurrentDateTime] = useState(new Date());

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [snackbarText, setSnackbarText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [templatesModalOpen, setTemplatesModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const templateVariables = useMemo(
    () => extractTemplateVariables(selectedTemplate),
    [selectedTemplate]
  );

  const [pricing, setPricing] = useState(null);
  const [loadingPricing, setLoadingPricing] = useState(false);
  const pricingTimerRef = useRef(null);

  const [termsAccepted, setTermsAccepted] = useState(false);

  const [showErrors, setShowErrors] = useState(false);

  const [totalContactCount, setTotalContactCount] = useState(0);
  const [importReport, setImportReport] = useState(null);
  const [fileValidationResult, setFileValidationResult] = useState(null);
  const [creationModalOpen, setCreationModalOpen] = useState(false);

  const { saveDraft, loadDraft, clearDraft } = useCampaignDraft();

  // Restaurar borrador al montar si no es edición
  useEffect(() => {
    if (initialData) return; // No restaurar si es edición

    const draft = loadDraft();
    if (!draft) return;

    if (draft.formData) setFormData(draft.formData);
    if (draft.selectedTemplate) setSelectedTemplate(draft.selectedTemplate);
    if (draft.selectedContacts) setSelectedContacts(draft.selectedContacts);
    if (draft.selectedCategories) setSelectedCategories(draft.selectedCategories);
    if (draft.importReport) setImportReport(draft.importReport);
    if (draft.totalContactCount != null) setTotalContactCount(draft.totalContactCount);
    if (draft.activeStep != null) setActiveStep(draft.activeStep);
    if (draft.termsAccepted != null) setTermsAccepted(draft.termsAccepted);

    setSnackbarSeverity("info");
    setSnackbarText("Se restauraron los datos de tu campa\u00f1a en curso.");
    setSnackbarOpen(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-guardar borrador ante cambios relevantes
  useEffect(() => {
    if (initialData) return;

    saveDraft({
      formData,
      selectedTemplate: selectedTemplate
        ? { _id: selectedTemplate._id, name: selectedTemplate.name, category: selectedTemplate.category, language: selectedTemplate.language }
        : null,
      selectedContacts,
      selectedCategories,
      importReport,
      totalContactCount,
      termsAccepted,
      activeStep,
    });
  }, [
    formData,
    selectedTemplate,
    selectedContacts,
    selectedCategories,
    importReport,
    totalContactCount,
    termsAccepted,
    activeStep,
    initialData,
    saveDraft,
  ]);

  const loadPricing = useCallback(async () => {
    if (!selectedTemplate?.category || !formData.audienceType) {
      setPricing(null);
      return;
    }

    const hasFile = formData.audienceType === "file" && uploadedFile;

    if (!hasFile) {
      setPricing(null);
      return;
    }

    setLoadingPricing(true);

    try {
      const quote = await getPricingQuote({
        templateType: selectedTemplate.category,
        uploadType: formData.audienceType,
        contactsFile: uploadedFile,
      });
      // console.log("info: ", quote);

      setPricing(quote);
    } catch (error) {
      const message = error?.message || "Error al cargar el precio de la campaña";
      setSnackbarSeverity("error");
      setSnackbarText(message);
      setSnackbarOpen(true);

      console.error("Error al cargar pricing:", error);
      setPricing(null);
    } finally {
      setLoadingPricing(false);
    }
  }, [selectedTemplate, formData.audienceType, uploadedFile]);

  const handleServerValidate = useCallback(async (file, onProgressChange) => {
    const result = await validateContactFile({
      file,
      username: name,
      apiKey: gupshup_app_id,
      onProgressChange,
    });
    return result;
  }, [name, gupshup_app_id]);

  useEffect(() => {
    if (pricingTimerRef.current) {
      clearTimeout(pricingTimerRef.current);
    }

    pricingTimerRef.current = setTimeout(() => {
      loadPricing();
    }, 800);

    return () => {
      if (pricingTimerRef.current) {
        clearTimeout(pricingTimerRef.current);
      }
    };
  }, [loadPricing]);

  useEffect(() => {
    let interval;
    if (formData.sendType === "enviar_ahora") {
      interval = setInterval(() => {
        setCurrentDateTime(new Date());
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [formData.sendType]);

  useEffect(() => {
    if (pricing?.total_contacts) {
      setTotalContactCount(pricing.total_contacts);
    } else {
      setTotalContactCount(0);
    }
  }, [pricing]);

  // Pre-poblar formulario cuando se reciben datos para clonar
  useEffect(() => {
    if (!initialData) return;

    setFormData((prev) => ({
      ...prev,
      campaignName: initialData.campaignName || '',
      alias: initialData.alias || '',
      template: initialData.template || '',
      descripcion: initialData.descripcion || '',
      audienceType: initialData.audienceType || 'file',
    }));

    if (initialData.selectedTemplate) {
      setSelectedTemplate(initialData.selectedTemplate);
    }

    if (initialData.selectedContacts?.length > 0) {
      setSelectedContacts(initialData.selectedContacts);
    }

    if (initialData.uploadedFile) {
      setUploadedFile(initialData.uploadedFile);
    }

    if (initialData.selectedCategories?.length > 0) {
      setSelectedCategories(initialData.selectedCategories);
    }
  }, [initialData]);

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleAudienceChange = (audienceType) => {
    setFormData((prev) => ({
      ...prev,
      audienceType,
    }));
    setImportReport(null);
    setPricing(null);
  };

  const handleScheduleChange = (scheduleData) => {
    setFormData((prev) => ({
      ...prev,
      ...scheduleData,
    }));
  };

  const handleOpenContactsModal = () => setContactsModalOpen(true);
  const handleCloseContactsModal = () => setContactsModalOpen(false);
  const handleConfirmContacts = (contacts) => setSelectedContacts(contacts);
  const handleFileChange = (file) => setUploadedFile(file);

  const handleAudienceSnackbar = (message, severity) => {
    setSnackbarSeverity(severity);
    setSnackbarText(message);
    setSnackbarOpen(true);
  };

  const handleOpenTemplatesModal = () => setTemplatesModalOpen(true);

  const handleCloseTemplatesModal = () => setTemplatesModalOpen(false);

  const handleConfirmTemplate = (template) => {
    setSelectedTemplate(template);
    setFormData((prev) => ({
      ...prev,
      template: template._id,
    }));
    setTemplatesModalOpen(false);

    setPricing(null);
  };

  const requiredVersions = importReport?.estimated_templates_needed
    ?? (totalContactCount > 0
      ? Math.ceil(totalContactCount / MAX_CONTACTS_PER_TEMPLATE)
      : 1);

  const isBasicStepValid = () => {
    return (
      formData.campaignName &&
      formData.alias &&
      formData.template &&
      formData.descripcion &&
      formData.descripcion.length <= 400
    );
  };

  const isAudienceStepValid = () => {
    if (!formData.audienceType) return false;
    if (formData.audienceType === "file" && !uploadedFile) {
      return false;
    }
    return true;
  };

  const isScheduleStepValid = () => {
    if (!formData.sendType) return false;

    if (formData.sendType === "programar" && !formData.scheduledDateTime) {
      return false;
    }

    return true;
  };

  const isStepValid = (step) => {
    switch (step) {
      case 0:
        return isBasicStepValid();
      case 1:
        return isAudienceStepValid();
      case 2:
        return isScheduleStepValid();
      case 3:
        return termsAccepted;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (isStepValid(activeStep)) {
      setShowErrors(false);
      setActiveStep((prevStep) => prevStep + 1);
    } else {
      setShowErrors(true);
    }
  };

  const handleBack = () => {
    setShowErrors(false);
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleCreateCampaign = async ({ createdCount, hasClones }) => {
    setIsSubmitting(true);

    const programmingDate = formData.sendType === "enviar_ahora"
      ? null
      : formatApiDate(formData.scheduledDateTime);

    if (programmingDate && isProgrammingDateExpired(programmingDate)) {
      setIsSubmitting(false);
      return {
        success: false,
        error: "La fecha programada no puede ser anterior o igual a la hora actual en México. Selecciona una fecha futura."
      };
    }

    const uploadContactsType = formData.audienceType === 'file'
      ? UPLOAD_TYPES.FILE
      : UPLOAD_TYPES.CONTACTS;

    try {
      const idTemplates = createdCount > 0 && selectedTemplate?._id
        ? [formData.template, ...Array(createdCount).fill(selectedTemplate._id)]
        : undefined;

      await createCampaign({
        username: name,
        apiKey: gupshup_app_id,
        payload: {
          name: formData.campaignName,
          alias: formData.alias,
          description: formData.descripcion,
          id_template: formData.template,
          id_templates: idTemplates,
          upload_contacts_type: uploadContactsType,
          programming_date: programmingDate,
          id_customer,
          file: uploadedFile,
        },
      });

      clearDraft();
      setIsSubmitting(false);
      return { success: true, campaignName: formData.campaignName };
    } catch (error) {
      const message = error?.response?.data?.message || error?.message || "Error desconocido al crear la campaña";
      setIsSubmitting(false);
      return { success: false, error: message };
    }
  };

  const getTemplateName = () => {
    return selectedTemplate?.name || "";
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Información de la Campaña
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Completa los datos básicos de tu campaña
            </Typography>

            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                required
                label="Nombre de campaña"
                value={formData.campaignName}
                onChange={handleChange("campaignName")}
                placeholder="Ej: Campaña Verano 2026"
                size="small"
                error={showErrors && !formData.campaignName}
                helperText={
                  showErrors && !formData.campaignName
                    ? "El nombre de la campaña es requerido"
                    : " "
                }
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                required
                label="Alias"
                value={formData.alias}
                onChange={handleChange("alias")}
                placeholder="Ej: verano_2026"
                helperText={
                  showErrors && !formData.alias
                    ? "El alias es requerido"
                    : "Identificador único para la campaña"
                }
                error={showErrors && !formData.alias}
                size="small"
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body2"
                sx={{ mb: 0.5, color: "text.secondary", fontWeight: 500 }}
              >
                Template *
              </Typography>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleOpenTemplatesModal}
                endIcon={<SearchIcon />}
                sx={{
                  justifyContent: "space-between",
                  textTransform: "none",
                  color: selectedTemplate ? "text.primary" : "text.secondary",
                  py: 1,
                  px: 1.5,
                  borderColor: showErrors && !selectedTemplate ? "error.main" : undefined,
                  "&:hover": {
                    borderColor: showErrors && !selectedTemplate ? "error.main" : undefined,
                  },
                }}
              >
                {selectedTemplate?.name || "Haz clic para seleccionar un template"}
              </Button>
              {showErrors && !selectedTemplate && (
                <Typography variant="caption" color="error" sx={{ ml: 1.5, mt: 0.5, display: "block" }}>
                  Debes seleccionar un template
                </Typography>
              )}
            </Box>

            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                required
                multiline
                rows={4}
                label="Descripción"
                value={formData.descripcion}
                onChange={handleChange("descripcion")}
                placeholder="Describe brevemente el objetivo de esta campaña..."
                inputProps={{ maxLength: 400 }}
                helperText={
                  showErrors && !formData.descripcion
                    ? "La descripción es requerida"
                    : `${formData.descripcion.length}/400 caracteres`
                }
                error={showErrors && !formData.descripcion}
                size="small"
              />
            </Box>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Selecciona tu Audiencia
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Define a quién se enviará esta campaña
            </Typography>

            <AudienceSection
              value={formData.audienceType}
              onChange={handleAudienceChange}
              uploadedFile={uploadedFile}
              onFileChange={handleFileChange}
              pricing={pricing}
              loadingPricing={loadingPricing}
              onImportReport={setImportReport}
              importReport={importReport}
              onServerValidate={handleServerValidate}
              onSnackbar={handleAudienceSnackbar}
              selectedTemplate={selectedTemplate}
              onValidationResult={setFileValidationResult}
            />
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Programa el Envío
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Configura cuándo se enviará tu campaña
            </Typography>

            <ScheduleSection
              scheduleType={formData.scheduleType}
              sendType={formData.sendType}
              scheduledDateTime={formData.scheduledDateTime}
              currentDateTime={currentDateTime}
              onChange={handleScheduleChange}
            />
          </Box>
        );

      case 3:
        return (
          <Box>
            <SummarySection
              formData={formData}
              selectedContacts={selectedContacts}
              uploadedFile={uploadedFile}
              templateName={getTemplateName()}
              pricing={pricing}
              loadingPricing={loadingPricing}
              termsAccepted={termsAccepted}
              onAcceptTerms={setTermsAccepted}
              selectedCategories={selectedCategories}
              importReport={importReport}
              templateVariables={templateVariables}
              validationResult={fileValidationResult}
            />
          </Box>
        );

      default:
        return "Paso desconocido";
    }
  };

  return (
    <Box>
      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 2 }}>
        {steps.map((label, index) => (
          <Step key={label}>
            <StepButton
              onClick={() => {
                if (index < activeStep) {
                  setShowErrors(false);
                  setActiveStep(index);
                }
              }}
            >
              {label}
            </StepButton>
          </Step>
        ))}
      </Stepper>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          backgroundColor: "grey.50",
          borderRadius: 2,
        }}
      >
        {getStepContent(activeStep)}
      </Paper>

      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Box>
          <Button onClick={() => { clearDraft(); onCancel(); }} variant="outlined" disabled={isSubmitting}>
            Cancelar
          </Button>
        </Box>

        <Box sx={{ display: "flex", gap: 1 }}>
          {activeStep > 0 && (
            <Button onClick={handleBack} disabled={isSubmitting}>
              Atrás
            </Button>
          )}

          {activeStep === steps.length - 1 ? (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.5 }}>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => setCreationModalOpen(true)}
                disabled={!isStepValid(activeStep) || isSubmitting}
              >
                Crear Campaña
              </Button>
              {!isStepValid(activeStep) && !isSubmitting && (
                <Typography variant="caption" color="text.secondary">
                  Debes aceptar los términos y condiciones para crear la campaña
                </Typography>
              )}
            </Box>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!isStepValid(activeStep)}
            >
              Siguiente
            </Button>
          )}
        </Box>
      </Box>

      <TemplatesModal
        open={templatesModalOpen}
        onClose={handleCloseTemplatesModal}
        onConfirm={handleConfirmTemplate}
        initialSelectedId={formData.template}
      />

      <ContactsModal
        open={contactsModalOpen}
        onClose={handleCloseContactsModal}
        onConfirm={handleConfirmContacts}
        initialSelected={selectedContacts.map((c) => c.id)}
      />

      <CampaignCreationProgressModal
        open={creationModalOpen}
        onComplete={() => {
          setCreationModalOpen(false);
          if (onSubmit) {
            setTimeout(() => onSubmit(), 300);
          }
        }}
        onClose={() => setCreationModalOpen(false)}
        onCampaignCreated={handleCreateCampaign}
        selectedTemplate={selectedTemplate}
        requiredVersions={requiredVersions}
        totalContactCount={totalContactCount}
        name={name}
        gupshupAppId={gupshup_app_id}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarText}
        </Alert>
      </Snackbar>
    </Box>
  );
};
